import { useState, useEffect, CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookingDetails } from '../types';
import { getBooking, cancelBooking } from '../utils/booking-api';
import { detectUserTimezone } from '../utils/timezone';
import Button from '../components/Button';
import Textarea from '../components/Textarea';
import Loader from '../components/Loader';
import Modal from '../components/Modal';

function formatDateTime(isoString: string, timezone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      dateStyle: 'full',
      timeStyle: 'short'
    });
    return formatter.format(new Date(isoString));
  } catch {
    return isoString;
  }
}

export default function CancelPage() {
  const navigate = useNavigate();
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isCanceling, setIsCanceling] = useState(false);
  const [isCanceled, setIsCanceled] = useState(false);
  const displayTimezone = detectUserTimezone();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');

    if (!code) {
      setError('No confirmation code provided. Please check your email for the cancellation link.');
      setIsLoading(false);
      return;
    }

    // Fetch booking details
    getBooking(code)
      .then((bookingData) => {
        setBooking(bookingData);
        setIsLoading(false);
      })
      .catch((err: any) => {
        setError(err.message || 'Unable to load booking details');
        setIsLoading(false);
      });
  }, []);

  const handleCancel = async () => {
    if (!booking) return;

    setIsCanceling(true);
    setError(null);

    try {
      await cancelBooking({
        code: booking.code,
        reason: cancelReason.trim() || undefined,
        tz: detectUserTimezone()
      });

      setIsCanceled(true);
    } catch (err: any) {
      setError(err.message || 'Failed to cancel booking. Please try again.');
    } finally {
      setIsCanceling(false);
    }
  };

  const handleBookAnother = () => {
    navigate('/');
  };

  const containerStyles: CSSProperties = {
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
    padding: 'var(--spacing-2xl) var(--spacing-md)'
  };

  const cardStyles: CSSProperties = {
    background: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-2xl)',
    boxShadow: 'var(--shadow-md)',
    border: '1px solid var(--color-border)'
  };

  const headingStyles: CSSProperties = {
    fontSize: '1.875rem',
    fontWeight: 600,
    color: 'var(--na-navy)',
    marginBottom: 'var(--spacing-lg)',
    textAlign: 'center'
  };

  const sectionStyles: CSSProperties = {
    marginBottom: 'var(--spacing-xl)'
  };

  const labelStyles: CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
    marginBottom: 'var(--spacing-xs)',
    display: 'block'
  };

  const valueStyles: CSSProperties = {
    fontSize: '1rem',
    color: 'var(--color-text)',
    marginBottom: 'var(--spacing-md)'
  };

  const buttonGroupStyles: CSSProperties = {
    display: 'flex',
    gap: 'var(--spacing-md)',
    marginTop: 'var(--spacing-2xl)'
  };

  const successMessageStyles: CSSProperties = {
    textAlign: 'center',
    padding: 'var(--spacing-2xl)',
    background: 'var(--color-success-light, #e8f5e9)',
    borderRadius: 'var(--radius-md)',
    marginBottom: 'var(--spacing-xl)'
  };

  const successIconStyles: CSSProperties = {
    fontSize: '3rem',
    marginBottom: 'var(--spacing-md)'
  };

  const warningBoxStyles: CSSProperties = {
    background: 'var(--color-warning-light, #fff3e0)',
    border: '1px solid var(--color-warning, #ff9800)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--spacing-md)',
    marginBottom: 'var(--spacing-xl)',
    fontSize: '0.875rem',
    color: 'var(--color-text)'
  };

  if (isLoading) {
    return (
      <div style={containerStyles}>
        <div style={cardStyles}>
          <Loader text="Loading booking details..." />
        </div>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div style={containerStyles}>
        <div style={cardStyles}>
          <h1 style={headingStyles}>Error</h1>
          <p style={{ textAlign: 'center', color: 'var(--color-error)' }}>{error}</p>
          <div style={{ textAlign: 'center', marginTop: 'var(--spacing-xl)' }}>
            <Button onClick={handleBookAnother} variant="primary">
              Book a Meeting
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isCanceled) {
    return (
      <div style={containerStyles}>
        <div style={cardStyles}>
          <div style={successMessageStyles}>
            <div style={successIconStyles}>✓</div>
            <h1 style={headingStyles}>Booking Canceled</h1>
            <p>Your meeting has been successfully canceled.</p>
          </div>

          {booking && (
            <div style={sectionStyles}>
              <p style={valueStyles}>
                <strong>Canceled Booking:</strong> {booking.serviceName} with {booking.providerName}
              </p>
              <p style={valueStyles}>
                <strong>Was scheduled for:</strong> {formatDateTime(booking.slot.start, displayTimezone)}
              </p>
            </div>
          )}

          <div style={{ textAlign: 'center' }}>
            <Button onClick={handleBookAnother} variant="primary">
              Book Another Meeting
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  return (
    <div style={containerStyles}>
      <div style={cardStyles}>
        <h1 style={headingStyles}>Cancel Booking</h1>

        <div style={warningBoxStyles}>
          <strong>⚠️ Warning:</strong> This action cannot be undone. Your scheduled meeting will be permanently canceled.
        </div>

        <div style={sectionStyles}>
          <span style={labelStyles}>Service</span>
          <p style={valueStyles}>{booking.serviceName}</p>
        </div>

        <div style={sectionStyles}>
          <span style={labelStyles}>Provider</span>
          <p style={valueStyles}>{booking.providerName}</p>
        </div>

        <div style={sectionStyles}>
          <span style={labelStyles}>Scheduled Time</span>
          <p style={valueStyles}>{formatDateTime(booking.slot.start, displayTimezone)}</p>
        </div>

        <div style={sectionStyles}>
          <span style={labelStyles}>Contact</span>
          <p style={valueStyles}>
            {booking.contact.name}
            <br />
            {booking.contact.email}
            {booking.contact.phone && (
              <>
                <br />
                {booking.contact.phone}
              </>
            )}
          </p>
        </div>

        <div style={sectionStyles}>
          <Textarea
            label="Cancellation Reason (Optional)"
            placeholder="Why are you canceling? (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            rows={4}
          />
        </div>

        {error && (
          <p style={{ color: 'var(--color-error)', marginBottom: 'var(--spacing-md)', textAlign: 'center' }}>
            {error}
          </p>
        )}

        <div style={buttonGroupStyles}>
          <div style={{ flex: 1 }}>
            <Button
              onClick={handleBookAnother}
              variant="outline"
              disabled={isCanceling}
            >
              Keep Booking
            </Button>
          </div>
          <div style={{ flex: 1 }}>
            <Button
              onClick={handleCancel}
              variant="primary"
              disabled={isCanceling}
            >
              {isCanceling ? 'Canceling...' : 'Cancel Booking'}
            </Button>
          </div>
        </div>
      </div>

      {/* Loading Modal */}
      <Modal
        isOpen={isCanceling}
        onClose={() => {}}
        title="Canceling Booking"
      >
        <Loader text="Please wait..." />
      </Modal>
    </div>
  );
}
